import type { FlightStatsResult } from './flightStats';
import { getCoords, greatCircleDistance } from './airportCoords';

export interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string; // lucide icon name
  unlocked: boolean;
  progress?: number;
  target?: number;
}

interface FlightLike { date: string; departureAirport: string; arrivalAirport: string }

interface AchievementDef {
  id: string;
  title: string;
  description: string;
  icon: string;
  check: (stats: FlightStatsResult, friendCount: number, flights: FlightLike[], referralCount: number) => { unlocked: boolean; progress?: number; target?: number };
}

const DEFINITIONS: AchievementDef[] = [
  {
    id: 'first-wings',
    title: 'First Wings',
    description: '1+ flights logged',
    icon: '\u2708\uFE0F',
    check: (s) => ({ unlocked: s.totalFlights >= 1, progress: Math.min(s.totalFlights, 1), target: 1 }),
  },
  {
    id: 'globe-trotter',
    title: 'Globe Trotter',
    description: '10+ unique airports',
    icon: '\uD83E\uDDED',
    check: (s) => ({ unlocked: s.uniqueAirports.length >= 10, progress: s.uniqueAirports.length, target: 10 }),
  },
  {
    id: 'century-club',
    title: 'Century Club',
    description: '100+ flights',
    icon: '\uD83C\uDFC6',
    check: (s) => ({ unlocked: s.totalFlights >= 100, progress: s.totalFlights, target: 100 }),
  },
  {
    id: 'long-hauler',
    title: 'Long Hauler',
    description: 'A flight over 3,000nm',
    icon: '\uD83C\uDF05',
    check: (_s, _fc, flights) => {
      let maxNm = 0;
      for (const f of flights) {
        const from = getCoords(f.departureAirport);
        const to = getCoords(f.arrivalAirport);
        if (from && to) maxNm = Math.max(maxNm, greatCircleDistance(from, to));
      }
      return { unlocked: maxNm >= 3000, progress: Math.round(maxNm), target: 3000 };
    },
  },
  {
    id: 'jet-setter',
    title: 'Jet Setter',
    description: '50,000+ total nm',
    icon: '\uD83D\uDE80',
    check: (s) => ({ unlocked: s.totalNauticalMiles >= 50000, progress: s.totalNauticalMiles, target: 50000 }),
  },
  {
    id: 'million-miler',
    title: 'Million Miler',
    description: '1,000,000+ total nm',
    icon: '\uD83D\uDC8E',
    check: (s) => ({ unlocked: s.totalNauticalMiles >= 1000000, progress: s.totalNauticalMiles, target: 1000000 }),
  },
  {
    id: 'city-hopper',
    title: 'City Hopper',
    description: '5+ airports in one month',
    icon: '\uD83D\uDCCD',
    check: (_s, _fc, flights) => {
      const byMonth = new Map<string, Set<string>>();
      for (const f of flights) {
        const key = f.date.slice(0, 7); // YYYY-MM
        if (!byMonth.has(key)) byMonth.set(key, new Set());
        const set = byMonth.get(key)!;
        set.add(f.departureAirport);
        set.add(f.arrivalAirport);
      }
      const maxAirports = Math.max(0, ...[...byMonth.values()].map((s) => s.size));
      return { unlocked: maxAirports >= 5, progress: maxAirports, target: 5 };
    },
  },
  {
    id: 'world-explorer',
    title: 'World Explorer',
    description: '10+ countries visited',
    icon: '\uD83C\uDF0D',
    check: (s) => ({ unlocked: s.countriesVisited >= 10, progress: s.countriesVisited, target: 10 }),
  },
  {
    id: 'social-butterfly',
    title: 'Social Butterfly',
    description: '5+ friends',
    icon: '\uD83E\uDD8B',
    check: (_s, fc) => ({ unlocked: fc >= 5, progress: fc, target: 5 }),
  },
  {
    id: 'crew-recruiter',
    title: 'Crew Recruiter',
    description: '3+ referrals',
    icon: '\uD83E\uDD1D',
    check: (_s, _fc, _f, rc) => ({ unlocked: rc >= 3, progress: rc, target: 3 }),
  },
  {
    id: 'crew-captain',
    title: 'Crew Captain',
    description: '10+ referrals',
    icon: '\u2B50',
    check: (_s, _fc, _f, rc) => ({ unlocked: rc >= 10, progress: rc, target: 10 }),
  },
  {
    id: 'crew-ambassador',
    title: 'Crew Ambassador',
    description: '25+ referrals — unlocks lifetime premium',
    icon: '\uD83D\uDC51',
    check: (_s, _fc, _f, rc) => ({ unlocked: rc >= 25, progress: rc, target: 25 }),
  },
];

export function computeAchievements(
  stats: FlightStatsResult,
  friendCount: number,
  flights: { date: string; departureAirport: string; arrivalAirport: string }[],
  referralCount = 0,
): Achievement[] {
  return DEFINITIONS.map((def) => {
    const result = def.check(stats, friendCount, flights, referralCount);
    return {
      id: def.id,
      title: def.title,
      description: def.description,
      icon: def.icon,
      ...result,
    };
  });
}
